import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { GripVertical } from 'lucide-react';
import type { ConfigItem, ConfigPhase, ConfigCategory } from '../types';

const CATEGORY_LABELS: Record<ConfigCategory, string> = {
  'sla': 'Service Level Agreements',
  'queue': 'Queues',
  'approval': 'Approval Flows',
  'asset': 'Asset Management',
  'automation': 'Automation Rules',
  'notification': 'Notifications',
  'email': 'Email Configuration',
  'portal': 'Customer Portal',
  'request-type-group': 'Request Type Groups',
  'knowledge-base': 'Knowledge Base',
  'integration': 'Integrations',
  'csat': 'Customer Satisfaction (CSAT)',
  'dashboard': 'Reports & Dashboards',
  'field-configuration': 'Field Configurations',
  'service-catalog': 'Service Catalog',
  'team-settings': 'Team & Agent Settings'
};

const PHASE_LABELS = {
  1: 'Phase 1 (Core)',
  2: 'Phase 2 (Automation)',
  3: 'Phase 3 (Portal)',
  4: 'Phase 4 (Advanced)',
  5: 'Future Consideration'
};

interface DraggableConfigItemProps {
  item: ConfigItem & { effectivePhase?: ConfigPhase; effectiveEnabled?: boolean; override?: any };
  index: number;
  phase: ConfigPhase;
  onMoveItem: (itemId: string, fromPhase: ConfigPhase, toPhase: ConfigPhase, newIndex: number) => void;
  onReorderItem: (itemId: string, fromIndex: number, toIndex: number, phase: ConfigPhase) => void;
  onEdit?: (item: any) => void;
  onDelete?: (itemId: string) => void;
  onResetToDefault?: (itemId: string) => void;
  renderActions?: (item: any) => React.ReactNode;
  showDefaultPhase?: boolean;
}

interface DragItem {
  type: string;
  id: string;
  index: number;
  phase: ConfigPhase;
}

export function DraggableConfigItem({
  item,
  index,
  phase,
  onMoveItem,
  onReorderItem,
  onEdit,
  onDelete,
  onResetToDefault,
  renderActions,
  showDefaultPhase = false
}: DraggableConfigItemProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag, preview] = useDrag<DragItem, void, { isDragging: boolean }>({
    type: 'CONFIG_ITEM',
    item: { type: 'CONFIG_ITEM', id: item.id, index, phase },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver, canDrop }, drop] = useDrop<DragItem, void, { isOver: boolean; canDrop: boolean }>({
    accept: 'CONFIG_ITEM',
    hover: (draggedItem: DragItem, monitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = index;
      const dragPhase = draggedItem.phase;
      const hoverPhase = phase;

      // Don't replace items with themselves
      if (draggedItem.id === item.id) {
        return;
      }

      // Same phase - reordering
      if (dragPhase === hoverPhase && dragIndex !== hoverIndex) {
        // Determine rectangle on screen
        const hoverBoundingRect = ref.current.getBoundingClientRect();
        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        // Determine mouse position
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the item's height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }

        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }

        // Time to actually perform the action
        onReorderItem(draggedItem.id, dragIndex, hoverIndex, phase);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        draggedItem.index = hoverIndex;
      }
    },
    drop: (draggedItem: DragItem) => {
      const dragPhase = draggedItem.phase;
      const dropPhase = phase;

      // Moving between phases
      if (dragPhase !== dropPhase) {
        onMoveItem(draggedItem.id, dragPhase, dropPhase, index);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  // Connect drag and drop refs
  drag(ref);
  drop(preview(ref));

  const opacity = isDragging ? 0.4 : 1;
  const backgroundColor = isOver && canDrop ? 'bg-blue-50' : '';
  const effectivePhase = item.effectivePhase ?? item.defaultPhase;
  const effectiveEnabled = item.effectiveEnabled ?? true;

  return (
    <div
      ref={ref}
      style={{ opacity }}
      className={`flex items-start gap-3 p-3 border rounded-lg cursor-move transition-colors ${backgroundColor} ${
        !effectiveEnabled ? 'opacity-50 bg-muted/30' : ''
      }`}
    >
      <div className="flex items-center pt-1">
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h4>{item.name}</h4>
          <Badge variant="outline">{CATEGORY_LABELS[item.category]}</Badge>
          {item.coreFeature && (
            <Badge variant="secondary">Core</Badge>
          )}
          {item.requiresAssets && (
            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
              Requires Assets
            </Badge>
          )}
          {item.override && (
            <Badge className="bg-blue-100 text-blue-800 border-blue-300">
              Customized
            </Badge>
          )}
          {!effectiveEnabled && (
            <Badge variant="outline" className="border-red-300 text-red-700">
              Disabled
            </Badge>
          )}
          {showDefaultPhase && item.override && effectivePhase !== item.defaultPhase && (
            <Badge variant="outline" className="text-xs">
              Moved from {PHASE_LABELS[item.defaultPhase]}
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground">{item.description}</p>
        {item.override?.notes && (
          <p className="text-sm text-blue-700 mt-1 italic">
            Note: {item.override.notes}
          </p>
        )}
      </div>

      {renderActions ? (
        renderActions(item)
      ) : (
        <div className="flex gap-2 flex-shrink-0">
          {/* Default actions if no custom actions provided */}
        </div>
      )}
    </div>
  );
}
